/**
 * conscienceSensitivityEngine.js
 *
 * Detects when the pastoral situation involves active conscience tension —
 * when a person is wrestling with guilt, conviction, or moral self-assessment.
 *
 * This is NOT about ethics risk. Ethics handles danger signals.
 * This handles the interior conscience dimension: the person is aware
 * of something and it is pressing on them spiritually.
 *
 * When active, Kevia should shift to gentle_conviction mode:
 * acknowledge without amplifying, invite without pressuring.
 */

/**
 * @param {{
 *   text:    string,
 *   emotion: object|null,
 *   ethics:  object,
 *   intent:  string,
 * }} params
 * @returns {{
 *   isActive:    boolean,
 *   mode:        "gentle_conviction"|"standard",
 *   instruction: string|null,
 * }}
 */
export function assessConscienceSensitivity({ text, emotion, ethics, intent }) {
  const dom      = emotion?.dominantEmotion ?? "none";
  const detected = ethics?.detected ?? [];
  const t        = text ?? "";

  // Conscience-active emotional states
  const conscienceEmotions = ["shame", "guilt", "spiritual_confusion"];

  // Text patterns suggesting interior moral wrestling
  const wrestlingPatterns = [
    /siento que\s+(fall[eé]|fall[oó]|no\s+debería|debería)/i,
    /me\s+siento\s+(culpable|mal\s+por|avergonzado)/i,
    /no\s+s[eé]\s+si\s+(est[eé]\s+bien|hice\s+lo\s+correcto|deb[ií]a)/i,
    /me\s+pesa|me\s+arrepiento|quisiera\s+haber/i,
    /¿estuvo\s+bien|¿fue\s+lo\s+correcto|¿hice\s+mal/i,
    /conciencia|convicción\s+de|se[ñn]alamiento/i,
  ];

  const hasWrestlingText = wrestlingPatterns.some((p) => p.test(t));
  const hasConscEmot     = conscienceEmotions.includes(dom);
  const hasEthicsConvict = detected.includes("apostasy") || detected.includes("discipline");

  const isActive =
    hasWrestlingText ||
    hasConscEmot ||
    (hasEthicsConvict && (intent === "reflection" || intent === "prayer"));

  if (!isActive) {
    return { isActive: false, mode: "standard", instruction: null };
  }

  return {
    isActive: true,
    mode: "gentle_conviction",
    instruction:
      "Hay tensión de conciencia activa. Reconoce sin amplificar. No minimices ni dramatices. " +
      "Invita sin presionar. La persona ya siente el peso — no lo aumente.",
  };
}
